import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';

import {
  ContainerPage,
  Margin,
  ContainerNavigation,
  ContainerFooter,
  ContainerTinySectionDivider,
  ContainerTinyFooterDivider,
} from '_templates/Pages';
import colours from '_theming/colours';
import Curved from '_components/Curved';
import Footer from '_sections/Footer';
import Layout from '_components/Layout';
import NavigationBar from '_components/NavigationBar';
import TermsOfService from '_sections/TermsOfService/TermsOfService';
import useSiteMetadata from '_hooks/useSiteMetadata';

/**
 * Container for each section.
 */
const ContainerSection = styled.div`
  background-color: ${(props) => props.bgColor || colours.white};
  padding: 75px 0;
`;

/**
 * Terms of Service page.
 *
 * @param {object} props Component props.
 * @param {object} props.location Router Location object.
 * @returns {React.Component} Terms of Service page.
 */
const TermsOfServicePage = ({ location }) => {
  const { siteUrl } = useSiteMetadata();

  const pageUrl = `${siteUrl}${location.pathname}`;
  const title = 'Terms of Service';
  const description =
    'Agreement that sets forth the general terms and conditions of your use of the Pricematepay.com website and any of its products or services.';
  const jsonLD = JSON.stringify(
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Terms of Service',
      description: description,
      breadcrumb: {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@type': 'WebSite',
              '@id': siteUrl,
              name: 'Pricematepay',
            },
          },
          {
            '@type': 'ListItem',
            position: 2,
            item: {
              '@type': 'WebPage',
              '@id': pageUrl,
              name: 'Terms',
            },
          },
        ],
      },
      publisher: {
        '@type': 'Organization',
        name: 'Pricematepay',
        logo: {
          '@type': 'ImageObject',
          url: `${siteUrl}/images/logos/Pricematepay/LightBlue.png`,
        },
        url: siteUrl,
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'Sales',
          telephone: '[+1-647-494-3992]',
          email: 'sales@pricematepay.com',
          areaServed: ['CA', 'US'],
          availableLanguage: 'EN',
        },
      },
    },
    null,
    2
  );

  return (
    <Layout>
      <Helmet defer={false}>
        <title>{title} - Pricematepay</title>
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="description" content={description} />
        <script type="application/ld+json">{jsonLD}</script>
      </Helmet>

      <ContainerPage>
        <ContainerNavigation>
          <Margin>
            <NavigationBar />
          </Margin>
        </ContainerNavigation>

        <ContainerTinySectionDivider>
          <Curved preserveAspectRatio="none" fillColor={colours.daintree} />
        </ContainerTinySectionDivider>

        <ContainerSection bgColor={colours.white}>
          <Margin>
            <TermsOfService />
          </Margin>
        </ContainerSection>

        <ContainerTinyFooterDivider>
          <Curved preserveAspectRatio="none" fillColor={colours.daintree} />
        </ContainerTinyFooterDivider>

        <ContainerFooter bgColor={colours.daintree}>
          <Margin>
            <Footer />
          </Margin>
        </ContainerFooter>
      </ContainerPage>
    </Layout>
  );
};

export default TermsOfServicePage;
